import { useEffect, useState, Fragment } from "react";
import api from "@/lib/api";
import { fmtEuro, MONTHS_FR } from "@/lib/format";
import { PeriodSelector } from "@/components/PeriodSelector";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Loader2, PiggyBank, Gift, Plus, Trash2, Pencil, ChevronRight, ChevronDown, Copy, CalendarDays } from "lucide-react";
import { toast } from "sonner";

// [0] = Chaque mois · [] = Non précisé · [3,6,9] = mois spécifiques (répartition à parts égales)
const monthsLabel = (arr) => {
  const a = arr || [];
  if (a.includes(0)) return "Chaque mois";
  const spec = a.filter((m) => m >= 1 && m <= 12).sort((x, y) => x - y);
  if (spec.length === 0) return "Non précisé";
  return spec.map((m) => MONTHS_FR[m - 1].slice(0, 3)).join(", ");
};
const toMonths = (p) => (Array.isArray(p.months) ? p.months : (p.month === 0 ? [0] : (p.month == null ? [] : [p.month])));

function MonthMultiSelect({ value, onChange, testid }) {
  const recurring = value.includes(0);
  const spec = value.filter((m) => m >= 1 && m <= 12);
  const toggleMonth = (m) => {
    const has = spec.includes(m);
    const next = has ? spec.filter((x) => x !== m) : [...spec, m];
    onChange(next.sort((a, b) => a - b));
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" className="h-9 text-sm justify-start font-normal w-full" data-testid={`${testid}-trigger`}>
          <CalendarDays className="w-4 h-4 mr-1.5 text-muted-foreground shrink-0" />
          <span className="truncate">{monthsLabel(value)}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        <label className="flex items-center gap-2 pb-2 mb-2 border-b border-border text-sm cursor-pointer">
          <Checkbox checked={recurring} onCheckedChange={(c) => onChange(c ? [0] : [])} data-testid={`${testid}-recurring`} />
          Chaque mois
        </label>
        <div className="grid grid-cols-3 gap-1.5">
          {MONTHS_FR.map((m, i) => {
            const num = i + 1;
            const checked = !recurring && spec.includes(num);
            return (
              <label key={num} className={`flex items-center gap-1.5 text-xs cursor-pointer rounded px-1.5 py-1 hover:bg-secondary ${recurring ? "opacity-40 pointer-events-none" : ""}`}>
                <Checkbox checked={checked} onCheckedChange={() => toggleMonth(num)} data-testid={`${testid}-m${num}`} />
                {m.slice(0, 4)}
              </label>
            );
          })}
        </div>
        <p className="text-[10px] text-muted-foreground mt-2">Le montant est réparti à parts égales entre les mois cochés. Aucun mois = « Non précisé ».</p>
      </PopoverContent>
    </Popover>
  );
}

function PlannedBlock({ type, year, items, categories, reload }) {
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [months, setMonths] = useState([new Date().getMonth() + 1]);
  const [catId, setCatId] = useState("");
  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [paidDrafts, setPaidDrafts] = useState({});
  const [openId, setOpenId] = useState(null);
  const [detailTx, setDetailTx] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [addForms, setAddForms] = useState({});
  const list = items.filter((i) => i.type === type);
  const cats = categories.filter((c) => c.type === type);

  const resetForm = () => { setLabel(""); setAmount(""); setCatId(""); setMonths([new Date().getMonth() + 1]); setEditId(null); };
  const openAdd = () => { resetForm(); setOpen(true); };
  const startEdit = (p) => { setEditId(p.id); setLabel(p.label); setAmount(String(p.amount)); setMonths(toMonths(p)); setCatId(p.category_id || ""); setOpen(true); };

  const save = async () => {
    if (!label.trim() || !amount) { toast.error("Libellé et montant requis"); return; }
    setAdding(true);
    try {
      const payload = { type, label, amount: Number(amount), category_id: catId || null, months, year, done: editId ? undefined : false };
      if (editId) {
        const cur = list.find((i) => i.id === editId);
        await api.put(`/planned/${editId}`, { ...payload, done: cur ? cur.done : false });
        toast.success("Élément modifié");
      } else {
        await api.post("/planned", { ...payload, done: false });
        toast.success("Élément planifié ajouté");
      }
      resetForm(); setOpen(false); reload();
    } catch { toast.error("Erreur"); } finally { setAdding(false); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cet élément prévu ? Cette action est définitive.")) return; await api.delete(`/planned/${id}`); if (editId === id) resetForm(); reload(); toast.success("Élément supprimé"); };
  const dup = async (p) => { await api.post(`/planned/${p.id}/duplicate`, { year: year + 1 }); reload(); toast.success(`Dupliqué sur ${year + 1}`); };
  const savePaid = async (id, val) => { await api.patch(`/planned/${id}/paid`, { paid_manual: Number(val) || 0 }); reload(); };
  const loadDetail = async (id) => { const { data } = await api.get(`/planned/${id}/transactions`); setDetailTx((d) => ({ ...d, [id]: data })); };
  const toggleDetail = async (p) => {
    if (openId === p.id) { setOpenId(null); return; }
    setOpenId(p.id);
    const spec = toMonths(p).filter((m) => m >= 1 && m <= 12);
    const dm = spec.length ? spec[0] : (new Date().getMonth() + 1);
    setAddForms((f) => ({ ...f, [p.id]: f[p.id] || { date: `${p.year}-${String(dm).padStart(2, "0")}-01`, vendor: "", amount: "" } }));
    setDetailLoading(true);
    try { await loadDetail(p.id); } finally { setDetailLoading(false); }
  };
  const addTx = async (p) => {
    const f = addForms[p.id] || {};
    if (!f.amount || Number(f.amount) <= 0) { toast.error("Montant requis"); return; }
    await api.post("/transactions", { date: f.date, vendor: f.vendor || p.label, description: "", amount: Number(f.amount), category_id: p.category_id || null, type: p.type, owner: "Commun", region: "", member_ids: [], planned_id: p.id, source: "manual" });
    await loadDetail(p.id);
    setAddForms((ff) => ({ ...ff, [p.id]: { ...ff[p.id], vendor: "", amount: "" } }));
    reload();
    toast.success(p.type === "income" ? "Rentrée ajoutée" : "Dépense ajoutée");
  };
  const deleteTx = async (p, txId) => { await api.delete(`/transactions/${txId}`); await loadDetail(p.id); reload(); };

  return (
    <div data-testid={`planned-block-${type}`}>
      <div className="flex justify-end mb-4">
        <Button size="sm" onClick={openAdd} data-testid={`planned-open-add-${type}`}>
          <Plus className="w-4 h-4 mr-1" />{type === "income" ? "Ajouter une rentrée" : "Ajouter une dépense"}
        </Button>
      </div>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="max-h-[85vh] overflow-y-auto" data-testid={`planned-dialog-${type}`}>
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier l'élément prévu" : (type === "income" ? "Nouvelle rentrée prévue" : "Nouvelle dépense prévue")}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-1">
            <div className="space-y-1.5"><label className="text-sm font-medium">Libellé</label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Libellé" data-testid={`planned-label-${type}`} /></div>
            <div className="space-y-1.5"><label className="text-sm font-medium">Montant</label>
              <div className="relative"><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant" className="pr-6" data-testid={`planned-amount-${type}`} /><span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span></div></div>
            <div className="space-y-1.5"><label className="text-sm font-medium">Mois</label>
              <MonthMultiSelect value={months} onChange={setMonths} testid={`planned-month-${type}`} /></div>
            <div className="space-y-1.5"><label className="text-sm font-medium">Catégorie</label>
              <Select value={catId} onValueChange={setCatId}>
                <SelectTrigger data-testid={`planned-cat-${type}`}><SelectValue placeholder="Catégorie" /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOpen(false); resetForm(); }}>Annuler</Button>
            <Button onClick={save} disabled={adding} data-testid={`planned-add-${type}`}>{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : (editId ? "Enregistrer" : "Ajouter")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Libellé</TableHead>
            <TableHead className="w-24">Mois</TableHead>
            <TableHead>Catégorie</TableHead>
            <TableHead className="text-right w-24">Prévu</TableHead>
            <TableHead className="text-right w-24">{type === "income" ? "Déjà reçu" : "Déjà dépensé"}</TableHead>
            <TableHead className="text-right w-24">{type === "income" ? "Reste à recevoir" : "Reste à dépenser"}</TableHead>
            <TableHead className="w-16"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {list.length === 0 ? (
            <TableRow><TableCell colSpan={7} className="text-xs text-muted-foreground text-center py-6">Aucune ligne pour l'instant. Ajoutez-en une ci-dessus.</TableCell></TableRow>
          ) : list.map((p) => {
            const real = p.real || 0;
            const reste = p.amount - real;
            return (
            <Fragment key={p.id}>
            <TableRow className={editId === p.id ? "bg-secondary/40" : ""} data-testid={`planned-${p.id}`}>
              <TableCell className="font-medium cursor-pointer" onClick={() => toggleDetail(p)} data-testid={`planned-toggle-detail-${p.id}`}>
                <div className="flex items-center gap-1.5">
                  {openId === p.id ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  {p.label}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">{monthsLabel(toMonths(p))}</TableCell>
              <TableCell className="text-muted-foreground text-sm">{p.category_name && p.category_name !== "Non catégorisé" ? p.category_name : "—"}</TableCell>
              <TableCell className="text-right font-num">{fmtEuro(p.amount)}</TableCell>
              <TableCell className="text-right font-num" data-testid={`planned-real-${p.id}`}>
                {fmtEuro(real)}
                {(p.real_linked > 0 || p.paid_manual > 0) && (
                  <div className="text-[10px] text-muted-foreground font-normal mt-0.5">
                    {p.real_linked > 0 ? `${fmtEuro(p.real_linked)} liés` : ""}{p.real_linked > 0 && p.paid_manual > 0 ? " · " : ""}{p.paid_manual > 0 ? `${fmtEuro(p.paid_manual)} saisi` : ""}
                  </div>
                )}
              </TableCell>
              <TableCell className={`text-right font-num ${reste < 0 ? "text-destructive" : "text-emerald-600"}`} data-testid={`planned-reste-${p.id}`}>{fmtEuro(reste)}</TableCell>
              <TableCell>
                <div className="flex items-center justify-end gap-0.5">
                  {p.source === "project" ? (
                    <span className="text-[10px] uppercase tracking-wide text-muted-foreground border border-border rounded px-1.5 py-0.5" title="Ligne pilotée par un projet — modifiez ou supprimez depuis la page Projets" data-testid={`planned-project-badge-${p.id}`}>Projet</span>
                  ) : (
                    <>
                      <Button variant="ghost" size="icon" className="h-7 w-7" title={`Dupliquer sur ${year + 1}`} onClick={() => dup(p)} data-testid={`planned-duplicate-${p.id}`}><Copy className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEdit(p)} data-testid={`planned-edit-${p.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => del(p.id)} data-testid={`planned-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
            {openId === p.id && (
              <TableRow className="bg-secondary/30 hover:bg-secondary/30">
                <TableCell colSpan={7} className="p-0">
                  <div className="px-4 py-3 space-y-1.5" data-testid={`planned-detail-${p.id}`}>
                    <div className="flex items-center gap-2 pb-2 mb-1 border-b border-border/40">
                      <span className="text-xs text-muted-foreground flex-1">Déjà {type === "income" ? "reçu" : "payé"} saisi manuellement (hors mouvements)</span>
                      {p.source === "project" ? (
                        <span className="text-xs text-muted-foreground italic" data-testid={`planned-paid-locked-${p.id}`}>Piloté par le projet · saisir via l'onglet Mensuel</span>
                      ) : (
                      <div className="relative w-28">
                        <Input type="number" value={paidDrafts[p.id] !== undefined ? paidDrafts[p.id] : (p.paid_manual || "")}
                          onChange={(e) => setPaidDrafts({ ...paidDrafts, [p.id]: e.target.value })}
                          onBlur={(e) => { if ((Number(e.target.value) || 0) !== (p.paid_manual || 0)) savePaid(p.id, e.target.value); }}
                          placeholder="0" className="h-8 text-right pr-5 font-num" data-testid={`planned-paid-${p.id}`} />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground text-xs">€</span>
                      </div>
                      )}
                    </div>
                    <div className="text-xs font-medium text-muted-foreground">Mouvements liés (imports & saisies)</div>
                    {detailLoading ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : (
                      (detailTx[p.id] || []).length === 0
                        ? <p className="text-xs text-muted-foreground">Aucun mouvement lié pour l'instant.</p>
                        : (detailTx[p.id] || []).map((t) => (
                          <div key={t.id} className="flex items-center gap-3 text-sm border-b border-border/40 py-1.5">
                            <span className="w-20 text-xs text-muted-foreground shrink-0">{(t.date || "").slice(0, 10)}</span>
                            <span className="flex-1 truncate">{t.vendor || "—"}</span>
                            <span className="font-num">{fmtEuro(t.amount)}</span>
                            {p.source !== "project" && (
                              <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => deleteTx(p, t.id)} data-testid={`planned-tx-del-${t.id}`}><Trash2 className="w-3 h-3" /></Button>
                            )}
                          </div>
                        ))
                    )}
                    {p.source === "project" ? (
                      <p className="text-[11px] text-muted-foreground pt-2 italic" data-testid={`planned-project-hint-${p.id}`}>Cette ligne est pilotée par un projet. Ajoutez les dépenses via l'onglet « Dépenses Mensuelles » en les rattachant au projet et à sa ligne.</p>
                    ) : (
                    <div className="flex items-end gap-2 pt-2">
                      <div className="w-36"><label className="text-[10px] text-muted-foreground">Date</label><Input type="date" value={addForms[p.id]?.date || ""} onChange={(e) => setAddForms({ ...addForms, [p.id]: { ...addForms[p.id], date: e.target.value } })} className="h-8" data-testid={`planned-add-date-${p.id}`} /></div>
                      <div className="flex-1"><label className="text-[10px] text-muted-foreground">Libellé</label><Input value={addForms[p.id]?.vendor || ""} onChange={(e) => setAddForms({ ...addForms, [p.id]: { ...addForms[p.id], vendor: e.target.value } })} placeholder={p.label} className="h-8" data-testid={`planned-add-vendor-${p.id}`} /></div>
                      <div className="w-28"><label className="text-[10px] text-muted-foreground">Montant</label><Input type="number" value={addForms[p.id]?.amount || ""} onChange={(e) => setAddForms({ ...addForms, [p.id]: { ...addForms[p.id], amount: e.target.value } })} placeholder="0" className="h-8 font-num" data-testid={`planned-add-amount-${p.id}`} /></div>
                      <Button size="sm" className="h-8" onClick={() => addTx(p)} data-testid={`planned-add-tx-${p.id}`}><Plus className="w-3.5 h-3.5 mr-1" />{p.type === "income" ? "Ajouter une rentrée" : "Ajouter une dépense"}</Button>
                    </div>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )}
            </Fragment>
          );})}
          {list.length > 0 && (
            <TableRow className="border-t-2 border-border bg-secondary/40 font-medium hover:bg-secondary/40" data-testid={`planned-total-row-${type}`}>
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell className="text-right font-num" data-testid={`planned-total-prevu-${type}`}>{fmtEuro(list.reduce((a, x) => a + (x.amount || 0), 0))}</TableCell>
              <TableCell className="text-right font-num" data-testid={`planned-total-real-${type}`}>{fmtEuro(list.reduce((a, x) => a + (x.real || 0), 0))}</TableCell>
              <TableCell className="text-right font-num" data-testid={`planned-total-reste-${type}`}>{fmtEuro(list.reduce((a, x) => a + ((x.amount || 0) - (x.real || 0)), 0))}</TableCell>
              <TableCell></TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default function Annual() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [planned, setPlanned] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/planned?year=${year}`).then((r) => setPlanned(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);
  useEffect(load, [year]);

  const sum = (t, doneOnly) => planned.filter((p) => p.type === t && (!doneOnly || p.done)).reduce((s, p) => s + p.amount, 0);
  const sumReal = (t) => planned.filter((p) => p.type === t).reduce((s, p) => s + (p.real || 0), 0);
  const totalExp = sum("expense", false);
  const totalInc = sum("income", false);
  const realExp = sumReal("expense"), realInc = sumReal("income");

  return (
    <div className="space-y-6" data-testid="annual-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">Vue Annuelle & Prévisions</h1>
          <p className="text-muted-foreground text-sm mt-1">Budgets provisionnels et rentrées exceptionnelles — {year}</p>
        </div>
        <PeriodSelector year={year} month={0} setYear={setYear} setMonth={() => {}} showMonth={false} />
      </div>

      {loading ? (
        <div className="h-64 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-6">
          <Card className="p-5 border-border" data-testid="annual-forecast-table">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><PiggyBank className="w-5 h-5" /></div>
              <h3 className="font-display font-semibold text-base">Budgets Prévisionnels</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Mazout, Cadeaux, Vacances, Précompte… — cochez quand c'est payé.</p>
            <div className="flex items-center justify-between text-sm pb-1 border-b border-border">
              <span className="text-muted-foreground">Total prévu</span>
              <span className="font-num font-semibold">{fmtEuro(totalExp)}</span>
            </div>
            <div className="flex items-center justify-between text-xs mb-3 mt-1 text-muted-foreground">
              <span>Déjà payé (réel)</span><span className="font-num">{fmtEuro(realExp)}</span>
            </div>
            <PlannedBlock type="expense" year={year} items={planned} categories={categories} reload={load} />
          </Card>

          <Card className="p-5 border-border" data-testid="annual-income-table">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-9 h-9 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center"><Gift className="w-5 h-5" /></div>
              <h3 className="font-display font-semibold text-base">Rentrées Exceptionnelles</h3>
            </div>
            <p className="text-xs text-muted-foreground mb-3">Dividendes, 13e mois, Droits d'auteur… — cochez quand c'est reçu.</p>
            <div className="flex items-center justify-between text-sm pb-1 border-b border-border">
              <span className="text-muted-foreground">Total attendu</span>
              <span className="font-num font-semibold text-emerald-600">{fmtEuro(totalInc)}</span>
            </div>
            <div className="flex items-center justify-between text-xs mb-3 mt-1 text-muted-foreground">
              <span>Déjà reçu (réel)</span><span className="font-num">{fmtEuro(realInc)}</span>
            </div>
            <PlannedBlock type="income" year={year} items={planned} categories={categories} reload={load} />
          </Card>
        </div>
      )}
    </div>
  );
}
